
import { Award, Medal, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";

const MeetYourCoach = () => {
  const certificationImages = [
    {
      src: "/lovable-uploads/bc9512dd-96c0-4d32-ac72-917d246207e7.png",
      alt: "Dr. Kevin Butler with Hornets Hoops"
    },
    {
      src: "/lovable-uploads/cf599f62-8ae3-4481-abcd-5b6758dc0566.png",
      alt: "NCAA Collegiate Compliance Credential"
    },
    {
      src: "/lovable-uploads/eedf510e-272f-4083-bbdd-86a966473c72.png",
      alt: "U.S. Safe Sports Trained Certification"
    },
    {
      src: "/lovable-uploads/ea13ecee-ab2f-49f9-acc1-e235b482a3b7.png",
      alt: "ACSI K-12 Athletic Director/School Counselor Certification"
    },
    {
      src: "/lovable-uploads/8912f95b-9bda-41be-9437-75833c511b7f.png",
      alt: "USA Basketball Gold Coach Licensed"
    },
    {
      src: "/lovable-uploads/cd6f03fc-6155-4ea1-87d6-c71cb80412ba.png",
      alt: "Basketball GM & Scouting Course Certificate"
    }
  ];

  return (
    <section className="py-12 sm:py-16 bg-white" id="meet-your-coach">
      <div className="container mx-auto px-4 sm:px-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-center text-primary mb-8 sm:mb-12">
          Meet Your Coach
        </h2>
        
        <div className="max-w-3xl mx-auto">
          {/* Coach Bio with Image */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-8">
            {/* Coach Image */}
            <div className="md:col-span-1">
              <AspectRatio ratio={1 / 1} className="bg-muted rounded-lg overflow-hidden">
                <img 
                  src="/lovable-uploads/e1998cc7-e07f-4563-9fc8-ef4ac2be7e3c.png" 
                  alt="Dr. Kevin Butler" 
                  className="object-cover w-full h-full"
                  onError={(e) => {
                    console.log("Image failed to load, trying fallback");
                    const target = e.target as HTMLImageElement;
                    target.src = "https://raw.githubusercontent.com/BTE2018/allstar-skill-quest/main/7778274987681951764.jpg";
                  }}
                />
              </AspectRatio>
            </div>
            
            {/* Bio Content */}
            <div className="md:col-span-2">
              <h3 className="text-xl sm:text-2xl font-bold text-secondary mb-3 sm:mb-4">Dr. Kevin Butler</h3>
              <p className="text-gray-700 mb-4 sm:mb-6 text-sm sm:text-base">
                Dr. Butler has over 40 years of basketball training experience as a former High School, Junior College, 
                Collegiate, Minor League Professional, and Professional Basketball coach for both females and males. 
                He specializes in Professional and Collegiate Basketball Training and has worked with an NBA agent 
                facilitating on-court free-agent tryout combines for NBA hopefuls. He also spent over 2 decades as a 
                college basketball recruiter, traveling the nation recruiting and training the best basketball talent.
              </p>
            </div>
          </div>
          
          {/* Credentials */}
          <h4 className="text-base sm:text-lg font-semibold text-primary mb-2 sm:mb-3">Professional Credentials:</h4>
          <ul className="space-y-1 sm:space-y-2 mb-4 sm:mb-6 text-sm sm:text-base">
            <li className="flex items-start gap-2">
              <Award className="h-4 w-4 sm:h-5 sm:w-5 text-secondary flex-shrink-0 mt-1" />
              <span>Member of the World Association of Basketball Coaches (FIBA)</span>
            </li>
            <li className="flex items-start gap-2">
              <Award className="h-4 w-4 sm:h-5 sm:w-5 text-secondary flex-shrink-0 mt-1" />
              <span>International Basketball Federation Coach Licensed</span>
            </li>
            <li className="flex items-start gap-2">
              <Award className="h-4 w-4 sm:h-5 sm:w-5 text-secondary flex-shrink-0 mt-1" />
              <span>U.S.A. Basketball Gold Coach Licensed</span>
            </li>
            <li className="flex items-start gap-2">
              <Award className="h-4 w-4 sm:h-5 sm:w-5 text-secondary flex-shrink-0 mt-1" />
              <span>National Association of Basketball Coaches</span>
            </li>
            <li className="flex items-start gap-2">
              <Award className="h-4 w-4 sm:h-5 sm:w-5 text-secondary flex-shrink-0 mt-1" />
              <span>Jr. NBA Certified</span>
            </li>
            <li className="flex items-start gap-2">
              <Award className="h-4 w-4 sm:h-5 sm:w-5 text-secondary flex-shrink-0 mt-1" />
              <span>Her Time To Play Facilitator</span>
            </li>
          </ul>

          {/* Certifications Gallery */}
          <div className="mb-6 sm:mb-8">
            <h3 className="text-xl sm:text-2xl font-bold text-center text-primary mb-6">Certifications & Credentials</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {certificationImages.map((image, index) => (
                <div key={index} className="rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow">
                  <img 
                    src={image.src} 
                    alt={image.alt}
                    className="w-full h-48 sm:h-56 object-contain bg-white p-2"
                  />
                </div>
              ))}
            </div>
          </div>
            
          {/* Success Quote */}
          <div className="p-3 sm:p-4 bg-primary/5 border-l-4 border-primary rounded mb-4 sm:mb-6">
            <p className="text-base sm:text-lg italic text-primary">
              "We Have Developed A History of Producing Diamonds From Coal"
            </p>
          </div>
            
          {/* Success Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Card className="bg-gradient-to-br from-primary/10 to-white border-primary/20">
              <CardContent className="pt-4 sm:pt-6 p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                  <Users className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                  <h5 className="font-semibold text-primary text-sm sm:text-base">First-Time Players</h5>
                </div>
                <p className="text-gray-700 text-xs sm:text-sm">
                  80% of our basketball trainees were either cut from a team, or have not played on a 
                  competitive basketball team prior to beginning in our program.
                </p>
              </CardContent>
            </Card>
              
            <Card className="bg-gradient-to-br from-secondary/10 to-white border-secondary/20">
              <CardContent className="pt-4 sm:pt-6 p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                  <Medal className="h-5 w-5 sm:h-6 sm:w-6 text-secondary" />
                  <h5 className="font-semibold text-secondary text-sm sm:text-base">Success Rate</h5>
                </div>
                <p className="text-gray-700 text-xs sm:text-sm">
                  We have a 97% success rate. Our success is driven by your results.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MeetYourCoach;
